/* Copyright (c) 2019 - 2021. Verimatrix. All Rights Reserved.
   All information in this file is Verimatrix Confidential and Proprietary. */

import React, { useEffect, useState } from "react";
import { useApolloClient } from "@apollo/react-hooks";
import {
  removeUser,
} from "./queries";
import { Table, Icon, Grid, Dropdown, Dimmer, Loader } from "semantic-ui-react";
import styles from "../MyAccount.module.css";
import {
  authCheckCallApi,
  getUserDescription,
  constructUrl,
  getUrlParam,
  setUrlParam,
  resetUrlParams,
  getUrlPath,
} from "../../Authenticator/utils";
import { MessageDialogParams } from "../../../types/MessageDialogParams";
import {
  DisableUserMutation,
  EnableUserMutation,
  User,
} from "../../../graphql/user";
import { getCountryName } from "../../Authenticator/countries";
import { ChangeEmailDialog } from "../Profile";
import ConfirmationDialog from "../../ConfirmationDialog";

interface UsersListProps {
  usersData: any;
  ro: boolean;
  onRefresh?: any;
  hiddenColumns?: string[];
  showSelectedOrg?: (org: string) => void;
}

const UsersList: React.FC<UsersListProps> = ({
  usersData,
  ro,
  onRefresh,
  hiddenColumns,
  showSelectedOrg,
}) => {
  const [message, setMessage] = useState<MessageDialogParams | null>(null);
  const [sorting, setSorting] = useState<any>({
    sortBy: "name",
    reverse: false,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [userPopup, setUserPopup] = useState<any>(null);
  const [showChangeEmail, setShowChangeEmail] = useState<any>(null);

  const client = useApolloClient();

  const showColumn = (columnName: string) =>
    !hiddenColumns || !hiddenColumns.includes(columnName);

  useEffect(() => {
    const userParam = getUrlParam("u");
    if (!!userParam && !!usersData && getUrlPath().endsWith("Users")) {
      const user = usersData.find((user: any) => user.email === userParam);
      if (user) {
        setUserPopup(user);
      } else {
        resetUrlParams();
      }
    }
  }, [usersData]);

  interface PendingUserAction {
    operation: string;
    mutation: any;
    variables: any;
    message: string;
  }
  const [pendingUserAction, setPendingUserAction] =
    useState<PendingUserAction | null>(null);

  const userAction = (operation: string, mutation: any, variables: any) => {
    setLoading(true);
    authCheckCallApi(() => {
      client
        .mutate({
          fetchPolicy: "no-cache",
          mutation: mutation,
          variables: variables,
        })
        .then(() => {
          setMessage({
            title: operation,
            message: `${operation} operation completed successfully.`,
          });
        })
        .catch((e) => {
          setMessage({
            title: operation,
            message: e.message,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  const processPendingUserAction = () => {
    if (!pendingUserAction) {
      return;
    }
    const operation = pendingUserAction.operation;
    const mutation = pendingUserAction.mutation;
    const variables = pendingUserAction.variables;
    setPendingUserAction(null);
    userAction(operation, mutation, variables);
  };

  const formatStatus = (status: string) =>
    status?.replace(/_/g, " ").toLocaleLowerCase() || "N/A";

  const isPlatform = (platform: boolean) =>
    platform ? "yes" : "";


  const userMenu = (user: User) => {
    const org = user.organizations[0];
    const status = formatStatus(org?.status);

    if (ro || user.platform) {
      return status;
    }

    return (
      <Dropdown
        pointing="top right"
        text={status}
        icon={{ className: "caret down" }}
      >
        <Dropdown.Menu>
          <Dropdown.Item
            text="Remove"
            icon="delete"
            onClick={() => {
              setPendingUserAction({
                operation: "Remove",
                mutation: removeUser,
                variables: { userId: user.id },
                message: "The user will be removed, are you sure?",
              });
            }}
          />

          <Dropdown.Item
            text="Change Email"
            icon="mail"
            onClick={() => {
              setShowChangeEmail({ userId: user.id, currentEmail: user.email });
            }}
          />

          {(org?.disabled && (
            <Dropdown.Item
              text="Enable"
              icon="redo"
              onClick={() => {
                setPendingUserAction({
                  operation: "Enable",
                  mutation: EnableUserMutation,
                  variables: { userId: user.id, orgId: org?.id },
                  message: "The user will be enabled, are you sure?",
                });
              }}
            />
          )) || (
            <Dropdown.Item
              text="Disable"
              icon="ban"
              onClick={() => {
                setPendingUserAction({
                  operation: "Disable",
                  mutation: DisableUserMutation,
                  variables: { userId: user.id, orgId: org?.id },
                  message: "The user will be disabled, are you sure?",
                });
              }}
            />
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const popupContent = (user: any) => {
    return (
      <Table celled>
        <Table.Body>
          <Table.Row key="name">
            <Table.Cell style={{ fontWeight: "700" }}>
              <Icon name="user" style={{ marginRight: "1rem" }} />
              {user.name}
            </Table.Cell>
          </Table.Row>

          <Table.Row key="email">
            <Table.Cell>Email</Table.Cell>
            <Table.Cell>{user.email}</Table.Cell>
          </Table.Row>

          <Table.Row key="org">
            <Table.Cell>Organization</Table.Cell>
            <Table.Cell className={styles.myAccountLink}>
              <div
                onClick={() => {
                  showSelectedOrg?.(user.organizations?.[0]?.name);
                  setUserPopup(null);
                }}
              >
                {user.organizations?.[0]?.name}
              </div>
            </Table.Cell>
          </Table.Row>

          <Table.Row key="role">
            <Table.Cell>Role</Table.Cell>
            <Table.Cell>
              {user.organizations?.[0]?.role}{" "}
              {user.organizations?.[0]?.disabled && " (disabled)"}
            </Table.Cell>
          </Table.Row>

          <Table.Row key="platform">
            <Table.Cell>Platform</Table.Cell>
            <Table.Cell>
              {isPlatform(user.platform)}
            </Table.Cell>
          </Table.Row>

          <Table.Row key="created">
            <Table.Cell>Created</Table.Cell>
            <Table.Cell>
              {user.created &&
                new Date(user.created).toLocaleDateString(navigator.language)}
            </Table.Cell>
          </Table.Row>

          <Table.Row key="lastLogin">
            <Table.Cell>Last Login</Table.Cell>
            <Table.Cell>
              {user.organizations?.[0]?.lastLogin &&
                new Date(user.organizations?.[0]?.lastLogin).toLocaleDateString(
                  navigator.language
                )}
            </Table.Cell>
          </Table.Row>

          <Table.Row key="status">
            <Table.Cell>Status</Table.Cell>
            <Table.Cell>
              {formatStatus(user.organizations?.[0]?.status)}
            </Table.Cell>
          </Table.Row>

          <Table.Row key="discount">
            <Table.Cell>Discount Code</Table.Cell>
            <Table.Cell>{user.discountCode}</Table.Cell>
          </Table.Row>

          <Table.Row key="tosApproved">
            <Table.Cell>Accepted terms and conditions date</Table.Cell>
            <Table.Cell>
              {user.tosApproved &&
                new Date(user.tosApproved).toLocaleDateString(
                  navigator.language
                )}
            </Table.Cell>
          </Table.Row>

          <Table.Row key="tosVersion">
            <Table.Cell>Accepted terms and conditions version</Table.Cell>
            <Table.Cell>
              {user.tosVersion && (
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  {new Date(user.tosVersion).toLocaleDateString(
                    navigator.language
                  )}
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`${constructUrl()}/tos/terms_${user.tosVersion}.html`}
                    className={styles.myAccountLink}
                  >
                    {" "}
                    Download this version
                  </a>
                </div>
              )}
            </Table.Cell>
          </Table.Row>

          <Table.Row key="line1">
            <Table.Cell>Address (Line 1)</Table.Cell>
            <Table.Cell>{user.address?.line1}</Table.Cell>
          </Table.Row>

          <Table.Row key="line2">
            <Table.Cell>Address (Line 2)</Table.Cell>
            <Table.Cell>{user.address?.line2}</Table.Cell>
          </Table.Row>

          <Table.Row key="city">
            <Table.Cell>City</Table.Cell>
            <Table.Cell>{user.address?.city}</Table.Cell>
          </Table.Row>

          <Table.Row key="state">
            <Table.Cell>State</Table.Cell>
            <Table.Cell>{user.address?.state}</Table.Cell>
          </Table.Row>

          <Table.Row key="zip">
            <Table.Cell>Zip</Table.Cell>
            <Table.Cell>{user.address?.zip}</Table.Cell>
          </Table.Row>

          <Table.Row key="country">
            <Table.Cell>Country</Table.Cell>
            <Table.Cell>
              {user.address && getCountryName(user.address.country)}
            </Table.Cell>
          </Table.Row>

          <Table.Row key="id">
            <Table.Cell>User ID</Table.Cell>
            <Table.Cell>{user.id}</Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
    );
  };

  const userRow = (user: User) => {
    const org = user.organizations[0];
    const country =
      (user.address && getCountryName(user.address.country)) ||
      (org && org.address && getCountryName(org.address.country));

    return (
      <Table.Row
        active={userPopup === user.id}
        key={user.id}
        style={{ cursor: "pointer" }}
        warning={!!org?.status && org.status.includes("PENDING")}
        error={
          !!org?.status && (org.status.includes("BLOCKED") || !!org.disabled)
        }
        positive={!!org?.status && org.status.includes("INVITED")}
        onClick={() => {
          if (getUrlPath().endsWith("Users")) {
            setUrlParam("u", user.email);
          }
          if (!window.getSelection()?.toString().length) {
            setUserPopup(user);
          }
        }}
      >
        <Table.Cell>{user.name}</Table.Cell>
        <Table.Cell>{user.email}</Table.Cell>
        <Table.Cell>{org?.name}</Table.Cell>
        <Table.Cell>{isPlatform(user.platform)}</Table.Cell>
        {showColumn("country") && (
          <Table.Cell className={styles.wordWrap}>{country}</Table.Cell>
        )}
        {showColumn("created") && (
          <Table.Cell className={styles.wordWrap}>
            {user.created &&
              new Date(user.created).toLocaleDateString(navigator.language)}
          </Table.Cell>
        )}
        {showColumn("lastLogin") && (
          <Table.Cell className={styles.wordWrap}>
            {org?.lastLogin &&
              new Date(org.lastLogin).toLocaleDateString(navigator.language)}
          </Table.Cell>
        )}

        {showColumn("role") && (
          <Table.Cell>
            {org?.role && getUserDescription(org.role)}
            {org?.disabled && " (disabled)"}
          </Table.Cell>
        )}

        {showColumn("discount") && <Table.Cell>{user.discountCode}</Table.Cell>}
        {showColumn("status") && <Table.Cell>{userMenu(user)}</Table.Cell>}
      </Table.Row>
    );
  };

  const sortUsers = (valueA: User, valueB: User) => {
    let sortValueA, sortValueB;
    const orgA = valueA.organizations[0];
    const orgB = valueB.organizations[0];

    switch (sorting.sortBy) {
      case "role":
        sortValueA = orgA && orgA.role;
        sortValueB = orgB && orgB.role;
        break;

      case "email":
        sortValueA = valueA.email;
        sortValueB = valueB.email;
        break;

      case "country":
        sortValueA =
          (valueA.address && valueA.address.country) ||
          (orgA && orgA.address && orgA.address.country);
        sortValueB =
          (valueB.address && valueB.address.country) ||
          (orgB && orgB.address && orgB.address.country);
        break;

      case "created":
        sortValueA = valueA.created;
        sortValueB = valueB.created;
        break;

      case "lastLogin":
        sortValueA = orgA && orgA.lastLogin;
        sortValueB = orgB && orgB.lastLogin;
        break;

      case "status":
        sortValueA = orgA && orgA.status;
        sortValueB = orgB && orgB.status;
        break;

      case "discountCode":
        sortValueA = valueA.discountCode;
        sortValueB = valueB.discountCode;
        break;

      case "organization":
        sortValueA = orgA && orgA.name;
        sortValueB = orgB && orgB.name;
        break;
      case "platform":
        sortValueA = isPlatform(valueA.platform);
        sortValueB = isPlatform(valueB.platform);
        break;
      default:
        sortValueA = valueA.name;
        sortValueB = valueB.name;
    }

    if (!sortValueA) {
      sortValueA = "";
    }
    if (!sortValueB) {
      sortValueB = "";
    }

    if (typeof sortValueA === "string") {
      sortValueA = sortValueA.toLocaleLowerCase();
    }
    if (typeof sortValueB === "string") {
      sortValueB = sortValueB.toLocaleLowerCase();
    }

    if (sortValueA < sortValueB) {
      return sorting.reverse ? 1 : -1;
    } else if (sortValueA > sortValueB) {
      return sorting.reverse ? -1 : 1;
    }
    return 0;
  };

  const setUserSorting = (sortBy: string) => {
    setSorting({
      sortBy: sortBy,
      reverse: sortBy === sorting.sortBy ? !sorting.reverse : false,
    });
  };

  const getSortIndicator = (columnName: string) => {
    if (sorting.sortBy === columnName) {
      return (
        <Icon
          name={sorting.reverse ? "triangle down" : "triangle up"}
          style={{ marginLeft: "3px" }}
        />
      );
    }
    return null;
  };

  if (usersData?.length > 0) {
    const sortedUsers = usersData.sort((userA: User, userB: User) =>
      sortUsers(userA, userB)
    );

    return (
      <React.Fragment>
        <Dimmer active={loading}>
          <Loader active={loading} size="large">
            Please wait...
          </Loader>
        </Dimmer>
        <Table
          selectable
          sortable
          celled
          compact="very"
          className={styles.usersTable}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                onClick={() => {
                  setUserSorting("name");
                }}
              >
                User Name
                {getSortIndicator("name")}
              </Table.HeaderCell>

              <Table.HeaderCell
                onClick={() => {
                  setUserSorting("email");
                }}
              >
                Email
                {getSortIndicator("email")}
              </Table.HeaderCell>

              <Table.HeaderCell
                onClick={() => {
                  setUserSorting("organization");
                }}
              >
                Organization
                {getSortIndicator("organization")}
              </Table.HeaderCell>

              <Table.HeaderCell
                onClick={() => {
                  setUserSorting("platform");
                }}
              >
                Platform
                {getSortIndicator("platform")}
              </Table.HeaderCell>

              {showColumn("country") && (
                <Table.HeaderCell
                  onClick={() => {
                    setUserSorting("country");
                  }}
                >
                  Country
                  {getSortIndicator("country")}
                </Table.HeaderCell>
              )}

              {showColumn("created") && (
                <Table.HeaderCell
                  onClick={() => {
                    setUserSorting("created");
                  }}
                >
                  Created
                  {getSortIndicator("created")}
                </Table.HeaderCell>
              )}

              {showColumn("lastLogin") && (
                <Table.HeaderCell
                  onClick={() => {
                    setUserSorting("lastLogin");
                  }}
                >
                  Last Login
                  {getSortIndicator("lastLogin")}
                </Table.HeaderCell>
              )}

              {showColumn("role") && (
                <Table.HeaderCell
                  onClick={() => {
                    setUserSorting("role");
                  }}
                >
                  Role
                  {getSortIndicator("role")}
                </Table.HeaderCell>
              )}

              {showColumn("discount") && (
                <Table.HeaderCell
                  onClick={() => {
                    setUserSorting("discountCode");
                  }}
                >
                  Discount
                  {getSortIndicator("discountCode")}
                </Table.HeaderCell>
              )}

              {showColumn("status") && (
                <Table.HeaderCell
                  onClick={() => {
                    setUserSorting("status");
                  }}
                >
                  Status
                  {getSortIndicator("status")}
                </Table.HeaderCell>
              )}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedUsers.map((user: any) => userRow(user))}
          </Table.Body>
        </Table>

        {message && (
          <ConfirmationDialog
            title={message.title}
            message={message.message}
            onOk={() => {
              setMessage(null);
              onRefresh && onRefresh();
            }}
          />
        )}

        {pendingUserAction && (
          <ConfirmationDialog
            title={pendingUserAction.operation}
            message={pendingUserAction.message}
            onOk={() => {
              processPendingUserAction();
            }}
            onCancel={() => {
              setPendingUserAction(null);
            }}
          />
        )}

        {showChangeEmail && (
          <ChangeEmailDialog
            currentEmail={showChangeEmail.currentEmail}
            userId={showChangeEmail.userId}
            onChangeEmailComplete={() => {
              setShowChangeEmail(false);
              onRefresh && onRefresh();
            }}
            disableTokenRefresh={true}
          />
        )}

        {userPopup && (
          <ConfirmationDialog
            title={"User Info"}
            message={""}
            onOk={() => {
              setUserPopup(null);
              resetUrlParams();
            }}
            optionalContent={() => popupContent(userPopup)}
            closeOnDimmerClick={true}
          />
        )}
      </React.Fragment>
    );
  }

  return (
    <Grid>
      <Grid.Column textAlign="center">
        <div className={styles.myAccountNoUsersLabel}>
          {(usersData && "No data.") || ""}
        </div>
      </Grid.Column>
    </Grid>
  );
};

export default UsersList;
